import { AB_PLATFORM_IGNORE, AB_PLATFORM_SSR } from '@src/config/constants'
import { CONFIG_DEBUG_EVENT_LOGGING, CONFIG_INTERNAL_ANALYTICS_ENDPOINT } from '@src/config/env'
import { getAssignedExperimentBuckets } from '@src/experiments'
import { AssignedExperimentBucket } from '@src/experiments/experiments.types'
import pageExperimentManager from '@src/experiments/pageExperiments'
import { statsigExperimentManager } from '@src/experiments/statsig-integration'
import { Filter } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import ProductTag from '@src/quince-site-common/src/domains/ProductTag'
import QuinceApiError from '@src/quince-site-common/src/domains/quince-api-error'
import appData from '@src/quince-site-common/src/helpers/appData'
import { isInNewCartExperiment } from '@src/quince-site-common/src/hooks/cart/hooks/new-cart-exp-helper'
import { getCartIDAndAuth, getCheckoutToken } from '@src/quince-site-common/src/hooks/cart/hooks/new-cart-helper'
import { LOCAL_STORAGE_CHECKOUT_ID_KEY } from '@src/quince-site-common/src/services/checkoutService/shopifyCheckoutService.constants'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { ANALYTIC_COOKIES } from '@src/quince-site-common/src/utils/constants'
import { getAppAnalyticsData } from '@src/quince-site-common/src/utils/cross-platform-analytics/cross-platform-analytics.utils'
import { decodeShopifyIdFromGraphqlIdResponse } from '@src/quince-site-common/src/utils/decodeShopifyIdFromGraphqlIdResponse'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { getFromLocalStorageWithExpiry } from '@src/quince-site-common/src/utils/getFromLocalStorageWithExpiry'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import { getPageContextDetails } from '@src/quince-site-common/src/utils/getPageContextDetails'
import { getProductTagsDataForBatchedAnalyticsFromTags } from '@src/quince-site-common/src/utils/getProductTagsDataForBatchedAnalytics'
import getUserDevice from '@src/quince-site-common/src/utils/getUserDevice'
import getUserDeviceCategory from '@src/quince-site-common/src/utils/getUserDeviceCategory'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import setCookie from '@src/quince-site-common/src/utils/setCookie'
import validateEmail from '@src/quince-site-common/src/utils/validateEmail'
import mixpanelCl from '@src/telemetry/integrations/mixpanel'
import { TelemetryIntegration } from '@src/telemetry/telemetry-events.types'
import { trackWithSgtm } from '@src/telemetry/trackWithSgtm'

import { TELEMETRY_EVENT_NAMES } from '../telemetry-events.constants'

import { vaildateLbUserId, vaildateSessionId } from './provider-utils/internal-analytics-utils'

// import {hasEstimatedDeliveryDate} from '../../organisms/shipmentTracking/earlyShipmentTimeLabel/earlyS// hippingTimeProductUtils';
let pageHash = ''

const inNewCartExperiment = isInNewCartExperiment()

export const InternalAnalytics: TelemetryIntegration = {
  config: {
    enabled: true,
    eventIdealTimeout: 1000,
    skipConsentCheck: true,
  },

  events: {
    [TELEMETRY_EVENT_NAMES.SESSION_CREATE]: properties => {
      const context = {
        lb_user_id: getlbUserId(),
        email: getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL),
        device_type: getUserDevice(),
      }

      logSessionStreamEvent({
        event_type: 'session_create',
        entity_type: 'session_id',
        entity_id: properties.sessionId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.NEW_USER_CREATED_RANDOM]: properties => {
      const context = {
        lb_user_id: getlbUserId(),
        email: getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL),
        device_type: getUserDevice(),
      }

      logSessionStreamEvent({
        event_type: 'new_user_created_random',
        entity_type: 'new_user_created_random_event',
        entity_id: getCookie(ANALYTIC_COOKIES.SESSION_ID) ?? getFromLocalStorageWithExpiry(ANALYTIC_COOKIES.SESSION_ID),
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.PRODUCT_VIEW]: properties => {
      if (!pageHash || pageHash != window.__unique_path_hash__) {
        pageHash = window.__unique_path_hash__
        const context = {
          ...properties?.context,
          product_id: properties.productId,
          isValentineProduct: properties.isValentineProduct,
          name: properties.productTitle,
          division: properties.division,
          subDepartment: properties.subDepartment,
          department: properties.department,
          gender: properties.gender,
        }

        logSessionStreamEvent({
          event_type: 'product_view',
          entity_type: 'product_variant',
          entity_id: properties.variantId,
          context,
          eventId: properties.eventId,
        })
        return
      }
      pageHash = window.__unique_path_hash__
    },

    [TELEMETRY_EVENT_NAMES.ADD_TO_CART]: properties => {
      const { cartID, authCode } = getCartIDAndAuth()
      const context = {
        ...properties?.context,
        product_id: properties.productId,
        line_items: properties.lineItems,
        tracker_section: properties.trackerSection,
        quantity: properties.quantity,
        checkout_token: getCheckoutToken(),
        is_preorder: properties.isPreorder,
        product_search_query: properties.productSearchQuery,
        product_position: properties.productPosition,
        source: properties.inventoryInfoSource,
        division: properties.division,
        department: properties.department,
        subDepartment: properties.subDepartment,
        // We get cart_upsell_${trackerConfigKey} as trackerSection for mobile view quick shop modal.
        quick_add_to_cart:
          properties?.trackerSection === 'quick_shop' ||
          properties?.trackerSection === 'social_section' ||
          properties?.trackerSection?.includes('cart_upsell_')
            ? 1
            : 0,
        color_variant: properties.color_variant,
        gender: properties.gender,
        cartID: cartID,
        authCode: authCode,
        productProperties: properties.productProperties,
      }

      logSessionStreamEvent({
        event_type: 'add_to_cart',
        entity_type: 'product_variant',
        entity_id: properties.variantId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.BUY_NOW]: (properties: any) => {
      const { cartID, authCode } = getCartIDAndAuth(inNewCartExperiment)
      const context = {
        ...properties?.context,
        product_id: properties.productId,
        line_items: (properties.lineItems || []).map((li: any) => ({
          ...li,
          productId: li?.productId?.toString(),
          variantId: li?.variantId?.toString(),
          price: li?.price?.toString(),
        })),
        tracker_section: properties.trackerSection,
        quantity: properties.quantity,
        checkout_token: getCheckoutToken(),
        is_preorder: properties.isPreorder,
        product_search_query: properties.productSearchQuery,
        product_position: properties.productPosition,
        source: properties.inventoryInfoSource,
        division: properties.division,
        department: properties.department,
        subDepartment: properties.subDepartment,
        color_variant: properties.color_variant,
        gender: properties.gender,
        cartID: cartID,
        authCode: authCode,
        productProperties: properties.productProperties,
      }
      logSessionStreamEvent({
        event_type: 'buy_now',
        entity_type: 'product_variant',
        entity_id: properties.variantId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.REMOVE_FROM_CART]: properties => {
      const { cartID, authCode } = getCartIDAndAuth()
      const context = {
        product_id: properties.productId,
        line_items: properties.lineItems,
        checkout_token: getCheckoutToken(),
        cartID: cartID,
        authCode: authCode,
        productProperties: properties.productProperties,
      }

      logSessionStreamEvent({
        event_type: 'remove_from_cart',
        entity_type: 'product_variant',
        entity_id: properties.variantId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.EMAIL_NEWSLETTER_SUBSCRIBE]: properties => {
      const context = {
        ...(properties.context || {}),
        email: properties.email,
        lb_user_id: getlbUserId(),
      }
      captureTentativeEmail(properties.email)
      if (properties.shouldSendWelcomeMail) {
        logSessionStreamEvent({
          event_type: 'klaviyo_newsletter_subscribe',
          entity_type: 'email',
          entity_id: properties.email,
          context,
          eventId: properties.eventId,
        })
      }
    },

    [TELEMETRY_EVENT_NAMES.SMS_NEWSLETTER_SUBSCRIBE]: properties => {
      if (properties.shouldSendWelcomeMail) {
        logSessionStreamEvent({
          event_type: 'sms_newsletter_subscribe',
          entity_type: 'phone',
          entity_id: properties.mobileNumber,
          context: {},
          eventId: properties.eventId,
        })
      }
    },

    [TELEMETRY_EVENT_NAMES.UPDATE_ITEM_QUANTITY]: properties => {
      const { cartID, authCode } = getCartIDAndAuth()
      const context = {
        product_id: properties.productId,
        line_items: properties.lineItems,
        checkout_token: getCheckoutToken(),
        cartID: cartID,
        authCode: authCode,
      }
      logSessionStreamEvent({
        event_type: 'update_item_quantity',
        entity_type: 'product_variant',
        entity_id: properties.variantId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.DIRECT_BUY_CLICK]: properties => {
      const context = {
        product_id: properties.productId,
        tracker_section: properties.trackerSection,
      }
      logSessionStreamEvent({
        event_type: 'direct_buy_click',
        entity_type: 'product_variant',
        entity_id: properties.variantId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.PAGE_SCROLL]: properties => {
      logSessionStreamEvent({
        event_type: 'page_scroll',
        entity_type: 'scroll_percentage',
        entity_id: properties.scrollPercentage,
        context: {},
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.ZOOM_PRODUCT_IMAGE]: properties => {
      const context = {
        productId: properties.productId,
        ...properties?.context,
      }
      //TODO : We dont currently get variant id here, need to finalize if we want at variant level or product level is suffice
      logSessionStreamEvent({
        event_type: 'product_thumbnail_zoom',
        entity_type: 'product_variant',
        entity_id: properties.variantId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.JOIN_THE_WAITLIST]: properties => {
      const context = {
        productId: properties.productId,
        email: properties.email,
        lb_user_id: getlbUserId(),
        ...properties?.context,
      }
      captureTentativeEmail(properties.email)
      logSessionStreamEvent({
        event_type: 'join_the_waitlist',
        entity_type: 'product_variant',
        entity_id: properties.variantId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.PAGE_VIEW]: properties => {
      logSessionStreamEvent({
        event_type: 'page_view',
        entity_type: 'page_path',
        entity_id: properties.pathname || window.location.pathname,
        context: properties.context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.HOME_PAGE_VIEW]: properties => {
      logSessionStreamEvent({
        event_type: 'home_page_view',
        entity_type: 'page_path',
        entity_id: properties.pathname || window.location.pathname,
        context: properties.context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.WISHLIST_PAGE_VIEW]: properties => {
      logSessionStreamEvent({
        event_type: 'wishlist_page_view',
        entity_type: 'page_path',
        entity_id: properties.pathname || window.location.pathname,
        context: properties.context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.SEARCH_PAGE_VIEW]: properties => {
      logSessionStreamEvent({
        event_type: 'search_page_view',
        entity_type: 'page_path',
        entity_id: properties.pathname || window.location.pathname,
        context: properties.context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.CART_PAGE_VIEW]: properties => {
      logSessionStreamEvent({
        event_type: 'cart_page_view',
        entity_type: 'page_path',
        entity_id: properties.pathname || window.location.pathname,
        context: properties.context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.COLLECTION_VIEW]: properties => {
      const variantLocationFunction =
        getUserDevice() === 'mobile' ? getVariantLocationInCollectionOnMobile : getVariantLocationInCollectionOnDesktop
      const context = {
        collection_id: properties?.collectionId,
        category_slug: properties.categorySlug,
        category_name: properties.categoryName,
        collection_slug: properties.collectionSlug,
        collection_name: properties.collectionName,
        subcollection_slug: properties.subcollectionSlug,
        subcollection_name: properties.subcollectionName,
        zipCode: properties.zipCode,
        variant_list: properties?.variantList?.map((variant_info: any) => {
          return {
            product_id: variant_info.product_id,
            variant_id: variant_info.variant_id,
            price: variant_info.price,
            labels: variant_info.labels,
            ...variantLocationFunction(variant_info.index),
          }
        }),
        filter_list: properties?.filterList?.filter((ft: Filter) => ft.values.length),
        isDynamicCollectionPage: properties.isDynamicCollectionPage,
      }
      const collectionName = properties.subcollectionName ? properties.subcollectionName : properties.collectionName
      logSessionStreamEvent({
        event_type: 'collection_view',
        entity_type: 'collection_name',
        entity_id: collectionName,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.SEND_REFERRAL_EMAIL_INVITE]: properties => {
      const context = {
        email: properties.email,
        lb_user_id: getlbUserId(),
      }
      captureTentativeEmail(properties.email)
      logSessionStreamEvent({
        event_type: 'send_referral_email_invite',
        entity_type: 'email',
        entity_id: properties.targetEmail,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.ACCORDION_TOGGLE]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
      }
      logSessionStreamEvent({
        event_type: 'accordion_toggle',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.BUTTON_CLICK]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
        productProperties: properties?.context?.productProperties,
      }

      //override default context values
      if (properties.context?.label) {
        context.label = properties.context.label
      }

      logSessionStreamEvent({
        event_type: 'button_click',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.LINK_CLICK]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        category_name: properties.context?.label ? [properties.context.label] : undefined,
        category_position:
          properties.context?.col !== undefined && properties.context.row !== undefined
            ? [properties.context.col * 2 + (properties.context.row + 1)]
            : undefined,
        label: properties.label,
        value: properties.value,
      }

      //override default context values
      if (properties.context?.label) {
        context.label = properties.context.label
      }

      logSessionStreamEvent({
        event_type: 'link_click',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.FORM_SUBMIT]: properties => {
      if (properties.action === 'account_login' || properties.action === 'account_create') {
        captureTentativeEmail(properties.context.login_email, false)
      }
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
        action: properties.action,
      }
      logSessionStreamEvent({
        event_type: 'form_submit',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.FORM_OPEN]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
      }
      logSessionStreamEvent({
        event_type: 'form_open',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.FORM_CLOSE]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
      }
      logSessionStreamEvent({
        event_type: 'form_close',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.MODAL_OPEN]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
      }
      logSessionStreamEvent({
        event_type: 'modal_open',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.MODAL_CLOSE]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
      }
      logSessionStreamEvent({
        event_type: 'modal_close',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.ENTER_FEEDBACK]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
      }
      logSessionStreamEvent({
        event_type: 'enter_feedback',
        entity_type: properties.entity_type,
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.SITE_ERROR]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
      }
      logSessionStreamEvent({
        event_type: 'site_error',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.ADD_TO_WISHLIST]: properties => {
      const context = {
        ...properties.context,
        product_id: properties.productId,
        color: properties.color,
        size: properties.size,
        line_items: properties.lineItems,
        tracker_section: properties.trackerSection,
        product_search_query: properties.product_search_query,
        product_position: properties.product_position,
        product_tags: properties.tags?.map((tag: ProductTag) => tag.value),
        count_color_variant: properties?.count_color_variant,
        department: properties?.department,
        department_id: properties?.department_id,
        division: properties?.division,
        division_id: properties?.division_id,
        rating: properties?.rating,
        rating_count: properties?.rating_count,
        subdepartment: properties?.subdepartment,
        subdepartment_id: properties?.subdepartment_id,
        title: properties?.title,
      }
      logSessionStreamEvent({
        event_type: 'add_to_wishlist',
        entity_type: 'product',
        entity_id: properties.productId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.WISHLIST_LOAD]: properties => {
      const context = {
        wishlist_product_list: properties.wishlistProductList,
      }
      logSessionStreamEvent({
        event_type: 'wishlist_loaded',
        entity_type: 'product',
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.SHARED_WISHLIST_LOAD]: properties => {
      const context = {
        wishlist_product_list: properties.wishlistProductList,
      }
      logSessionStreamEvent({
        event_type: 'shared_wishlist_loaded',
        entity_type: 'product',
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.SHARE_WISHLIST]: properties => {
      const context = {
        wishlist_product_list: properties.wishlistProductList,
        wishlist_token: properties.wishlistToken,
      }
      logSessionStreamEvent({
        event_type: 'share_wishlist',
        entity_type: properties.wishlistToken,
        entity_id: properties.wishlistToken,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.REMOVE_FROM_WISHLIST]: properties => {
      const context = {
        ...properties.context,
        product_id: properties.productId,
        color: properties.color,
        size: properties.size,
        line_items: properties.lineItems,
        tracker_section: properties.trackerSection,
        product_tags: properties.tags?.map((tag: ProductTag) => tag.value),
      }
      logSessionStreamEvent({
        event_type: 'remove_from_wishlist',
        entity_type: 'product',
        entity_id: properties.productId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.VIDEO_LOAD]: properties => {
      const context = {
        productId: properties.productId,
        color: properties.color,
      }
      logSessionStreamEvent({
        event_type: 'load_video',
        entity_type: 'product',
        entity_id: properties.productId,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.STREAMING_VIDEO_LOAD]: properties => {
      const context = {
        ...properties.context,
      }
      logSessionStreamEvent({
        event_type: 'load_streaming_video',
        entity_type: 'action',
        entity_id: 'homepage',
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.STREAMING_VIDEO_ERROR]: properties => {
      const context = {
        ...properties.context,
      }
      logSessionStreamEvent({
        event_type: 'error_streaming_video',
        entity_type: 'action',
        entity_id: 'homepage',
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.FILTER_CLICK]: properties => {
      const context = {
        ...properties.context,
        label: properties.label,
        value: properties.value,
      }

      //override default context values
      if (properties.context.label) {
        context.label = properties.context.label
      }

      logSessionStreamEvent({
        event_type: 'filter_click',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.API_RESPONSE]: properties => {
      const context = {
        ...properties.context,
        status: properties.status,
        inputRequest: properties.inputRequest,
        response: properties.response,
      }
      logSessionStreamEvent({
        event_type: 'api_response',
        entity_type: 'api_name',
        entity_id: properties.apiName,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.ORDER_CANCEL]: properties => {
      const context = {
        ...properties.context,
      }
      logSessionStreamEvent({
        event_type: 'order_cancel',
        entity_type: 'order_id',
        entity_id: properties.entityId,
        context,
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.PRODUCT_CLICK]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
      }
      //override default context values
      if (properties.context.label) {
        context.label = properties.context.label
      }

      logSessionStreamEvent({
        event_type: 'product_click',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.VISUAL_CUE_CLICK]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
      }
      logSessionStreamEvent({
        event_type: 'visual_cue_click',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.IS_VISIBLE]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
      }
      logSessionStreamEvent({
        event_type: 'is_visible',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.IS_VIDEO_WATCHED]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
      }
      logSessionStreamEvent({
        event_type: 'is_video_watched',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.IDME_VERIFICATION]: properties => {
      const context = {
        ...properties.context,
        category: properties.category,
        label: properties.label,
        value: properties.value,
      }
      logSessionStreamEvent({
        event_type: 'idme_verification',
        entity_type: 'action',
        entity_id: properties.action,
        context,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.PREORDER_INFO_CLICK]: properties => {
      logSessionStreamEvent({
        event_type: 'preorder_info_click',
        entity_id: properties.variantId,
        eventId: properties.eventId,
      })
    },

    // temp log tracking for vue data match
    [TELEMETRY_EVENT_NAMES.RECOMMENDATION_VIEW](properties) {
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.RECOMMENDATION_VIEW,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.CAROUSEL_LEFT_CLICK](properties) {
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.CAROUSEL_LEFT_CLICK,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.CAROUSEL_RIGHT_CLICK](properties) {
      logSessionStreamEvent({
        event_type: TELEMETRY_EVENT_NAMES.CAROUSEL_RIGHT_CLICK,
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.SUBCOLLECTION_CARD_CLICK]: properties => {
      const context = {
        subcollection_slug: properties.slug,
        subcollection_title: properties.title,
      }
      logSessionStreamEvent({
        entity_type: 'subcollection_card',
        eventId: properties.eventId,
        event_type: 'subcollection_card_click',
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW]: properties => {
      const { productList, ...remainingProps } = properties
      const updatedProductList = productList.map(product => {
        if (product.productProperties) {
          const { productProperties, ...restProps } = product
          const { product_id: _product_id, ...productPropertiesWithoutId } = productProperties // eslint-disable-line no-console
          return {
            ...restProps,
            ...productPropertiesWithoutId,
          }
        }
        return product
      })
      const context = {
        ...remainingProps,
        ...getProductTagsDataForBatchedAnalyticsFromTags(updatedProductList || []),
        collection_slug: properties.collectionSlug,
        products_list: updatedProductList,
        product_count: updatedProductList.length || 0,
        contentContainerType: properties.contentContainerType,
        contentContainerName: properties.contentContainerName,
        contentContainerDisplayName: properties.contentContainerDisplayName,
      }
      logSessionStreamEvent({
        event_type: 'is_visible',
        entity_type: 'product_card_view',
        entity_id: 'visible_product_list',
        context,
        eventId: properties.eventId,
      })
    },
    [TELEMETRY_EVENT_NAMES.VARIANT_SELECT]: properties => {
      const context = {
        variantIds: properties.variantIds,
        product_id: properties.product_id,
        internal_variant_ids: properties.internalVariantIds,
        product_options: properties.product_options,
        product_options_all: properties.product_options_all,
        tags: properties.tags,
        stock_info: properties.stock_info,
        color: properties.color,
        is_product_card: properties.is_product_card,
        division: properties.division,
        department: properties.department,
        subdepartment: properties.subdepartment,
        product_option_name: properties.product_option_name,
        product_option_value: properties.product_option_value,
        element_name: properties.element_name,
        element_display_name: properties.element_display_name,
        element_position: properties.element_position,
        element_type: properties.element_type,
        element_id: properties.element_id,
        ...properties?.context,
      }
      logSessionStreamEvent({
        event_type: 'variant_select',
        entity_id: properties.entity_id,
        entity_type: properties.entity_type || 'variant_id',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.TF_WIDGET_OPEN]: properties => {
      const context = {
        productId: properties.styleId,
        elementId: properties.elementId,
      }
      logSessionStreamEvent({
        event_type: 'tf_modal',
        entity_type: 'modal_open',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.TF_WIDGET_LOAD]: properties => {
      const context = {
        productId: properties.styleId,
        tfSuggestion: properties.successType,
        elementId: properties.elementId,
      }
      logSessionStreamEvent({
        event_type: 'tf_widget_suggestion',
        entity_type: 'widget_suggestion_on_load',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.TF_SIZE_RECOMMENDATION]: properties => {
      const context = {
        productId: properties.styleId,
        size: properties.size,
        elementId: properties.elementId,
      }
      logSessionStreamEvent({
        event_type: 'tf_widget_suggestion',
        entity_type: 'suggestion',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.TF_EXPERIMENTS]: properties => {
      const context = {
        productId: properties.productId,
        colorId: properties.colorId,
        elementId: properties.elementId,
        truefitRegistrationTreatment: properties.truefitRegistrationTreatment,
      }
      logSessionStreamEvent({
        event_type: 'tf_widget_experiments',
        entity_type: 'experiments',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.CLICKED_TO_SEARCH]: properties => {
      logSessionStreamEvent({
        event_type: 'clicked_to_search',
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.CUSTOMER_ID_SYNC_ERROR]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'customer_id_sync_failed',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.SEARCH_TRIGGERED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'search_triggered',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.TOP_NAV_OPENED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'top_nav_opened',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.PROFILE_MENU_OPENED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'profile_menu_opened',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.HEADER_LOGO_CLIKED]: properties => {
      logSessionStreamEvent({
        event_type: 'header_logo_clicked',
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.SORT_OPENED]: properties => {
      logSessionStreamEvent({
        event_type: 'sort_opened',
        eventId: properties.eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.SORT_USED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'sort_used',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.FAQ_VIEWED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'faq_viewed',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.FAQ_CLICKED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'faq_clicked',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.ADDRESS_ADDITION_INITIATED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'address_addition_initiated',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.ADDRESS_ADDITION_SUCCESSFUL]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'address_addition_successful',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.ADDRESS_EDIT_SUCCESSFUL]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'address_edit_successful',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.PRODUCT_GALLERY_VIEWED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'product_gallery_viewed',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.REVIEWS_SORTED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'reviews_sorted',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.SEARCH_COMPLETED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'search_completed',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.SIMILAR_COLLECTION_CLICKED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'similar_collection_clicked',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.SUBCOLLECTION_FILTER_USED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'subcollection_filter_used',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.VARIANT_SELECTED]: properties => {
      const context = properties
      logSessionStreamEvent({
        event_type: 'variant_selected',
        eventId: properties.eventId,
        context,
      })
    },

    [TELEMETRY_EVENT_NAMES.DO_NOT_TRACK]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'do_not_track',
        eventId: properties.eventId as string,
        entity_type: 'is_tracking_enabled',
        entity_id: context.trackingEnabled as boolean,
      })
    },
    [TELEMETRY_EVENT_NAMES.PRODUCT_GALLERY_SCROLLED]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'product_gallery_scrolled',
        eventId: properties.eventId as string,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.PRODUCT_CARD_SCROLLED]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'product_card_scrolled',
        eventId: properties.eventId,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.RETURN_LINE_ITEMS_SCREEN_VIEW]: properties => {
      const context: Record<string, unknown> = properties
      logSessionStreamEvent({
        event_type: 'return_line_items_screen_viewed',
        eventId: properties.eventId,
        entity_id: properties.entity_id,
        entity_type: properties.entity_type,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.RETURN_FEE_TOOLTIP_HOVER]: properties => {
      const context: Record<string, unknown> = properties

      logSessionStreamEvent({
        event_type: 'return_fee_tooltip_hover',
        eventId: properties.eventId,
        entity_id: properties.entity_id,
        entity_type: properties.entity_type,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.SUBMIT_LINE_ITEMS_CTA_CLICK]: properties => {
      const context: Record<string, unknown> = properties

      logSessionStreamEvent({
        event_type: 'submit_line_items_cta_clicked',
        eventId: properties.eventId,
        entity_id: properties.entity_id,
        entity_type: properties.entity_type,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.REFUND_AMOUNT_SCREEN_VIEW]: properties => {
      const context: Record<string, unknown> = properties

      logSessionStreamEvent({
        event_type: 'refund_amount_screen_viewed',
        eventId: properties.eventId,
        entity_id: properties.entity_id,
        entity_type: properties.entity_type,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.RETURN_CONFIRM_CTA_CLICK]: properties => {
      const context: Record<string, unknown> = properties

      logSessionStreamEvent({
        event_type: 'return_confirm_cta_clicked',
        eventId: properties.eventId,
        entity_id: properties.entity_id,
        entity_type: properties.entity_type,
        context,
      })
    },
    [TELEMETRY_EVENT_NAMES.RETURN_CONFIRM_SCREEN_VIEW]: properties => {
      const context: Record<string, unknown> = properties

      logSessionStreamEvent({
        event_type: 'return_confirm_screen_viewed',
        eventId: properties.eventId,
        entity_id: properties.entity_id,
        entity_type: properties.entity_type,
        context,
      })
    },
  },
}

// Note: We will now no longer have cart details in any event as we dont get this info from checkout graphql api
const logSessionStreamEvent = ({
  event_type,
  entity_type,
  entity_id,
  context,
  cart_details,
  eventId,
  pageUrl,
}: {
  event_type: any
  entity_type?: any
  entity_id?: any
  context?: any
  cart_details?: any
  eventId: string
  pageUrl?: string
}) => {
  cart_details = cart_details || {}
  const { context: appContextData, ...appRestEventData } = getAppAnalyticsData()
  context = {
    ...appContextData,
    ...context,
    isPartOfNewSiteLaunchExperiment: getCookie(ANALYTIC_COOKIES.IS_PART_OF_NEW_SITE_LAUNCH_EXPERIMENT),
    bucketForNewSiteLaunchExperiment: getCookie(ANALYTIC_COOKIES.BUCKET_FOR_NEW_SITE_LAUNCH_EXPERIMENT),
  }
  let email = getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL) || getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_TENTATIVE_EMAIL)
  if (email) {
    email = email.toLowerCase()
  }

  const currentDomain = `${location.origin}/`

  const { currentPage, previousPage } = getPageContextDetails()

  vaildateLbUserId()
  vaildateSessionId()

  const event_data = {
    ...appRestEventData,
    id: eventId,
    event_type: event_type,
    entity_type: entity_type,
    entity_id: entity_id,
    context: context,
    session_id: getCookie(ANALYTIC_COOKIES.SESSION_ID) || getFromLocalStorageWithExpiry(ANALYTIC_COOKIES.SESSION_ID),
    lb_user_id: getlbUserId(),
    email: email,
    device: getUserDevice(),
    device_category: getUserDeviceCategory(),
    url: pageUrl ?? window.location.href,
    campaign: {
      source: getCookie(ANALYTIC_COOKIES.UTM_SOURCE),
      medium: getCookie(ANALYTIC_COOKIES.UTM_MEDIUM),
      campaign: getCookie(ANALYTIC_COOKIES.UTM_CAMPAIGN),
      content: getCookie(ANALYTIC_COOKIES.UTM_CONTENT),
      term: getCookie(ANALYTIC_COOKIES.UTM_TERM),
    },
    thirdPartyAnalyticsData: {
      metaPixel: {
        fbc: getCookie('_fbc'),
        fbp: getCookie('_fbp'),
        external_id: getlbUserId(),
      },
    },
    event_time: new Date(),
    cart_token: cart_details.token,
    is_logged_in: AuthUtil.isLoggedIn(),
    active_experiments: getActiveExperiments(),
    referrer: isBrowser() ? (window.__client_side_navigation__ ? currentDomain : document.referrer) : '',
    qclid: getCookie(ANALYTIC_COOKIES.QCLID),
    app_environment_type: appData.environmentType.toUpperCase(),
    page_name: currentPage.page_name,
    page_type: currentPage.page_type,
    previous_page_name: previousPage.page_name,
    previous_page_type: previousPage.page_type,
    zip_code: getCookie(ANALYTIC_COOKIES.USER_ENTERED_ZIP_CODE) || getCookie(ANALYTIC_COOKIES.ZIP_CODE),
  }
  if (CONFIG_DEBUG_EVENT_LOGGING) {
    console.log('Internal Analytics', event_data)
  }
  fetch(CONFIG_INTERNAL_ANALYTICS_ENDPOINT, {
    method: 'POST',
    mode: 'cors',
    credentials: 'same-origin',
    headers: {
      'Content-type': 'application/json',
    },
    redirect: 'follow',
    referrer: 'no-referrer',
    body: JSON.stringify(event_data),
  }).catch((err: unknown) => {
    const quinceError = new QuinceApiError({
      error: err as Error,
      message: 'InternalAnalytics api failed',
      apiName: 'InternalAnalytics track',
    })
    captureException(quinceError, {
      feature: FEATURES.TRACK_EVENT,
      module: MODULES.INTERNAL_ANALYTICS,
      extraInfo: {
        capturedFrom: 'logSessionStreamEvent in InternalAnalytics',
      },
    })
  })
  trackWithSgtm({ eventData: event_data }).catch((err: unknown) => {
    throw err
  })
  // mixpanelCl.track(event_data)
  mixpanelCl.trackWithSgtm(event_data)
}

const captureTentativeEmail = (email: string, isTentativeEmail = true) => {
  const emailCookie = getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL)
  if (!emailCookie && email && validateEmail(email)) {
    setCookie(ANALYTIC_COOKIES.LB_CUSTOMER_TENTATIVE_EMAIL, email)
    mixpanelCl.identify({ email, isTentativeEmail })
  }
}

// Mimicking what is done in the render code of collection page.
const getVariantLocationInCollectionOnDesktop = (index: number) => {
  if (index < 3) {
    return { row: 0, col: index }
  }
  if (index < 7) {
    return { row: 1, col: index - 3 }
  }
  if (index === 7) {
    return { row: 3, col: 0 }
  }
  if (index < 12) {
    return { row: 4, col: index - 8 }
  }
  if (index < 14) {
    return { row: 5, col: index - 12 }
  }
  if (index < 17) {
    return { row: 6, col: index - 14 }
  }
  const loadMoreIndex = index - 17
  const block = parseInt(loadMoreIndex / 7)
  const blockIndex = loadMoreIndex % 7
  const row = 6 + block * 2 + (blockIndex < 4 ? 1 : 2)
  const col = blockIndex < 4 ? blockIndex : blockIndex - 4
  return {
    row,
    col,
  }
}

// Mimicking what is done in the render code of collection page.
const getVariantLocationInCollectionOnMobile = (index: number) => {
  const block = parseInt(index / 3)
  const blockIndex = index % 3
  const row = block * 2 + (blockIndex < 2 ? 0 : 1)
  const col = blockIndex < 2 ? blockIndex : blockIndex - 2
  return {
    row,
    col,
  }
}

const shouldTrackBucketForUser = (experiment: AssignedExperimentBucket): boolean => {
  const loggedInCondition = !experiment.loggedInUsersOnly || (experiment.loggedInUsersOnly && AuthUtil.isLoggedIn())

  return loggedInCondition
}

const getAssignedExperimentBucketsForLogging = () => {
  const assignedExperimentBuckets = getAssignedExperimentBuckets()
  const experimentsToTrack = Object.values(assignedExperimentBuckets).filter(shouldTrackBucketForUser)
  const assignedExpLogs = experimentsToTrack.map(experiment => ({
    experimentName: experiment.name,
    bucketName: experiment.bucket,
  }))

  return assignedExpLogs
}

const getIsPlatformSSR = (): string[] => {
  if (!isBrowser()) return []
  const platformCookie = getCookie(ANALYTIC_COOKIES.PLATFORM_AB)
  if (platformCookie?.includes('ssr')) return [AB_PLATFORM_SSR]
  if (platformCookie?.includes('ignore')) return [AB_PLATFORM_IGNORE]
  return []
}

const getActiveExperiments = () => {
  const activeExperiments = []

  const experimentBuckets = [
    ...getAssignedExperimentBucketsForLogging(),
    ...pageExperimentManager.getUserBucketsForEventLogging(),
    ...statsigExperimentManager.getExposedBuckets(),
  ]
  for (const experimentBucket of experimentBuckets) {
    activeExperiments.push(`${experimentBucket.experimentName}_${experimentBucket.bucketName}`)
  }

  return [...activeExperiments, ...getIsPlatformSSR()]
}

export const extractShopifyCheckoutToken = () => {
  return decodeShopifyIdFromGraphqlIdResponse(window.localStorage.getItem(LOCAL_STORAGE_CHECKOUT_ID_KEY))?.split('?')[0]
}

export const trackWebsiteAction = ({ actionName, payload = {} }: { actionName: string; payload?: object }) => {
  try {
    logSessionStreamEvent({
      event_type: 'website_action',
      entity_type: 'action_name',
      entity_id: actionName,
      context: payload,
      eventId: '',
    })
  } catch (e) {}
}
